/**
 * Category-to-default-method mapping for unit type inference.
 * When a user selects a category, we auto-infer the most common measurement method.
 * The user can always override via the "Change" link.
 */

/**
 * Maps activity category keys to their default measurement method.
 * Categories not listed here will show the full method selector.
 */
export const CATEGORY_METHOD_DEFAULTS: Record<string, string> = {
  // Scope 1 categories
  stationary_combustion: 'Energy',
  mobile_combustion: 'Distance',
  fugitive_emissions: 'Weight',
  process_emissions: 'Weight',
  // Scope 2 categories
  purchased_electricity: 'Energy',
  purchased_steam: 'Energy',
  purchased_heating: 'Energy',
  purchased_cooling: 'Energy',
  // Common Scope 3 categories
  business_travel: 'Distance',
  employee_commuting: 'Distance',
  upstream_transportation: 'WeightOverDistance',
  downstream_transportation: 'WeightOverDistance',
  waste_generated: 'Weight',
  purchased_goods: 'Money',
  capital_goods: 'Money',
  fuel_and_energy: 'Energy',
};

/**
 * Category-to-default-unit mapping for create-mode quantity inputs.
 * These defaults favor business-facing units over the first raw Climatiq option.
 */
export const CATEGORY_UNIT_DEFAULTS: Record<string, Record<string, string>> = {
  stationary_combustion: { energy_unit: 'kWh' },
  mobile_combustion: { distance_unit: 'km' },
  process_emissions: { weight_unit: 'kg' },
  fugitive_emissions: { weight_unit: 'kg' },
  purchased_electricity: { energy_unit: 'kWh' },
  purchased_steam: { energy_unit: 'kWh' },
  purchased_heating: { energy_unit: 'kWh' },
  purchased_cooling: { energy_unit: 'kWh' },
};

/**
 * Human-readable labels for method names.
 * Replaces raw Climatiq method names like "WeightOverDistance" with user-friendly text.
 */
export const METHOD_HUMAN_LABELS: Record<string, string> = {
  Area: 'Measured by area',
  AreaOverTime: 'Measured by area over time',
  ContainerOverDistance: 'Measured by containers over distance',
  Data: 'Measured by data volume',
  DataOverTime: 'Measured by data over time',
  Distance: 'Measured by distance',
  DistanceOverTime: 'Measured by distance over time',
  Energy: 'Measured by energy',
  Money: 'Measured by monetary spend',
  Number: 'Measured by count',
  NumberOverTime: 'Measured by count over time',
  PassengerOverDistance: 'Measured by passengers over distance',
  Power: 'Measured by power',
  Time: 'Measured by time',
  Volume: 'Measured by volume',
  Weight: 'Measured by weight',
  WeightOverDistance: 'Measured by weight over distance',
  WeightOverTime: 'Measured by weight over time',
};

/**
 * Short human-readable labels for the compact "Measuring by: X" display.
 */
export const METHOD_SHORT_LABELS: Record<string, string> = {
  Area: 'Area',
  AreaOverTime: 'Area / Time',
  ContainerOverDistance: 'Containers / Distance',
  Data: 'Data',
  DataOverTime: 'Data / Time',
  Distance: 'Distance',
  DistanceOverTime: 'Distance / Time',
  Energy: 'Energy',
  Money: 'Spend',
  Number: 'Count',
  NumberOverTime: 'Count / Time',
  PassengerOverDistance: 'Passengers / Distance',
  Power: 'Power',
  Time: 'Time',
  Volume: 'Volume',
  Weight: 'Weight',
  WeightOverDistance: 'Weight / Distance',
  WeightOverTime: 'Weight / Time',
};

/**
 * Get the default method for a given activity category.
 * Returns undefined if no default exists (user must select manually).
 */
export function getDefaultMethodForCategory(category: string): string | undefined {
  return CATEGORY_METHOD_DEFAULTS[category];
}

/**
 * Get the default unit for a given activity category and input key.
 * Returns undefined if no category-specific default exists.
 */
export function getDefaultUnitForCategory(
  category: string,
  unitKey: string
): string | undefined {
  return CATEGORY_UNIT_DEFAULTS[category]?.[unitKey];
}
